import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { format } from 'date-fns'
import { patientHistoryService } from '../services/patientHistoryService'
import { ConsentFormData, PatientConsent, ConsentType, ConsentStatus } from '../types/patientHistory'
import { Modal } from './Modal'
import './ConsentManagement.css'

interface ConsentManagementProps {
  patientId: string
  consents: PatientConsent[]
}

export function ConsentManagement({ patientId, consents }: ConsentManagementProps) {
  const queryClient = useQueryClient()
  const [showConsentForm, setShowConsentForm] = useState(false)
  const [editingConsent, setEditingConsent] = useState<PatientConsent | null>(null)
  const [showWithdrawModal, setShowWithdrawModal] = useState<string | null>(null)

  const deleteMutation = useMutation({
    mutationFn: (consentId: string) => patientHistoryService.removeConsent(patientId, consentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
    },
  })

  const withdrawMutation = useMutation({
    mutationFn: ({ consentId, reason }: { consentId: string; reason?: string }) =>
      patientHistoryService.withdrawConsent(patientId, consentId, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
      setShowWithdrawModal(null)
    },
  })

  const getStatusBadge = (status: ConsentStatus) => {
    const styles = {
      granted: { label: 'Granted', className: 'badge-granted' },
      withdrawn: { label: 'Withdrawn', className: 'badge-withdrawn' },
      expired: { label: 'Expired', className: 'badge-expired' },
      pending: { label: 'Pending', className: 'badge-pending' },
      refused: { label: 'Refused', className: 'badge-refused' },
    }
    const style = styles[status] || styles.pending
    return <span className={`badge ${style.className}`}>{style.label}</span>
  }

  const getConsentTypeLabel = (type: ConsentType) => {
    const labels: Record<ConsentType, string> = {
      'general-treatment': 'General Treatment',
      'specific-procedure': 'Specific Procedure',
      'surgery': 'Surgery',
      'medication': 'Medication',
      'diagnostic-test': 'Diagnostic Test',
      'data-sharing': 'Data Sharing',
      'research-participation': 'Research Participation',
      'emergency-treatment': 'Emergency Treatment',
      'other': 'Other',
    }
    return labels[type] || type
  }

  return (
    <div className="consent-management">
      {consents.length === 0 ? (
        <div className="consents-empty">
          <p>No consent records yet.</p>
        </div>
      ) : (
        <div className="consents-list">
          {consents.map((consent) => (
            <div key={consent.id} className="consent-item">
              <div className="consent-header">
                <div className="consent-title">
                  <h3>{getConsentTypeLabel(consent.type)}</h3>
                  {getStatusBadge(consent.status)}
                </div>
                <div className="consent-actions">
                  {consent.status === 'granted' && (
                    <button
                      className="btn-link btn-warning"
                      onClick={() => setShowWithdrawModal(consent.id)}
                    >
                      Withdraw
                    </button>
                  )}
                  <button
                    className="btn-link"
                    onClick={() => {
                      setEditingConsent(consent)
                      setShowConsentForm(true)
                    }}
                  >
                    Edit
                  </button>
                  <button
                    className="btn-link btn-danger"
                    onClick={() => {
                      if (window.confirm('Delete this consent record?')) {
                        deleteMutation.mutate(consent.id)
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </div>

              <div className="consent-description">{consent.description}</div>

              <div className="consent-details">
                {consent.grantedDate && (
                  <div className="consent-detail">
                    <strong>Granted:</strong>{' '}
                    {format(new Date(consent.grantedDate), 'dd MMM yyyy')}
                  </div>
                )}
                {consent.withdrawnDate && (
                  <div className="consent-detail">
                    <strong>Withdrawn:</strong>{' '}
                    {format(new Date(consent.withdrawnDate), 'dd MMM yyyy')}
                  </div>
                )}
                {consent.expiryDate && (
                  <div className="consent-detail">
                    <strong>Expires:</strong>{' '}
                    {format(new Date(consent.expiryDate), 'dd MMM yyyy')}
                  </div>
                )}
              </div>

              <div className="consent-authority">
                <div className="authority-section">
                  <strong>Patient Can Consent:</strong>{' '}
                  {consent.canPatientConsent ? 'Yes' : 'No'}
                </div>

                {!consent.canPatientConsent && consent.proxyConsent && (
                  <div className="authority-section">
                    <strong>Proxy Consent:</strong>
                    <div className="proxy-consent-details">
                      <div>
                        <strong>Given by:</strong> {consent.proxyConsent.givenBy}
                      </div>
                      <div>
                        <strong>Relationship:</strong> {consent.proxyConsent.relationship}
                      </div>
                      {consent.proxyConsent.contactInfo && (
                        <div>
                          <strong>Contact:</strong> {consent.proxyConsent.contactInfo}
                        </div>
                      )}
                      {consent.proxyConsent.authorityDocument && (
                        <div>
                          <strong>Authority:</strong> {consent.proxyConsent.authorityDocument}
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {consent.treatmentAuthority && (
                  <div className="authority-section">
                    <strong>Treatment Authority:</strong>
                    <div className="treatment-authority-details">
                      {consent.treatmentAuthority.practitionerName && (
                        <div>
                          <strong>Practitioner:</strong> {consent.treatmentAuthority.practitionerName}
                        </div>
                      )}
                      {consent.treatmentAuthority.scope && (
                        <div>
                          <strong>Scope:</strong> {consent.treatmentAuthority.scope}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {consent.restrictions && consent.restrictions.length > 0 && (
                <div className="consent-restrictions">
                  <strong>Restrictions:</strong>{' '}
                  {consent.restrictions.join(', ')}
                </div>
              )}

              {consent.notes && (
                <div className="consent-notes">
                  <strong>Notes:</strong> {consent.notes}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <button
        className="btn-primary btn-small"
        onClick={() => {
          setEditingConsent(null)
          setShowConsentForm(true)
        }}
        style={{ marginTop: '16px' }}
      >
        + Add Consent
      </button>

      {showConsentForm && (
        <ConsentForm
          patientId={patientId}
          initialData={editingConsent || undefined}
          onSuccess={() => {
            setShowConsentForm(false)
            setEditingConsent(null)
            queryClient.invalidateQueries({ queryKey: ['patient-clinical-history', patientId] })
          }}
          onCancel={() => {
            setShowConsentForm(false)
            setEditingConsent(null)
          }}
        />
      )}

      {showWithdrawModal && (
        <WithdrawConsentModal
          consentId={showWithdrawModal}
          onConfirm={(reason) => {
            withdrawMutation.mutate({ consentId: showWithdrawModal, reason })
          }}
          onCancel={() => setShowWithdrawModal(null)}
          isSubmitting={withdrawMutation.isPending}
        />
      )}
    </div>
  )
}

// Consent Form Component
interface ConsentFormProps {
  patientId: string
  initialData?: PatientConsent
  onSuccess: () => void
  onCancel: () => void
}

function ConsentForm({ patientId, initialData, onSuccess, onCancel }: ConsentFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<ConsentFormData>({
    defaultValues: initialData
      ? {
          type: initialData.type,
          description: initialData.description,
          status: initialData.status,
          grantedDate: initialData.grantedDate || '',
          expiryDate: initialData.expiryDate || '',
          canPatientConsent: initialData.canPatientConsent,
          proxyConsent: initialData.proxyConsent,
          treatmentAuthority: initialData.treatmentAuthority,
          notes: initialData.notes || '',
          restrictions: initialData.restrictions || [],
        }
      : {
          status: 'granted',
          canPatientConsent: true,
        },
  })

  const canPatientConsent = watch('canPatientConsent')
  const updateMutation = useMutation({
    mutationFn: (data: ConsentFormData | Partial<ConsentFormData>) => {
      if (initialData) {
        return patientHistoryService.updateConsent(patientId, initialData.id, data)
      }
      return patientHistoryService.addConsent(patientId, data as ConsentFormData)
    },
    onSuccess: () => {
      onSuccess()
    },
  })

  const onSubmit = (data: ConsentFormData) => {
    // Process restrictions as comma-separated string
    const restrictionsValue = (data.restrictions as string | string[] | undefined)
    const processedData = {
      ...data,
      restrictions:
        typeof restrictionsValue === 'string'
          ? restrictionsValue.split(',').map((r: string) => r.trim()).filter(Boolean)
          : (restrictionsValue || []) as string[],
    }
    updateMutation.mutate(processedData)
  }

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title={initialData ? 'Edit Consent' : 'Add Consent'}
      size="large"
    >
      <form onSubmit={handleSubmit(onSubmit)} className="consent-form">
        <div className="form-group">
          <label htmlFor="type">
            Consent Type <span className="required">*</span>
          </label>
          <select
            id="type"
            {...register('type', { required: true })}
            className={errors.type ? 'error' : ''}
          >
            <option value="general-treatment">General Treatment</option>
            <option value="specific-procedure">Specific Procedure</option>
            <option value="surgery">Surgery</option>
            <option value="medication">Medication</option>
            <option value="diagnostic-test">Diagnostic Test</option>
            <option value="data-sharing">Data Sharing</option>
            <option value="research-participation">Research Participation</option>
            <option value="emergency-treatment">Emergency Treatment</option>
            <option value="other">Other</option>
          </select>
        </div>

        <div className="form-group">
          <label htmlFor="description">
            Description <span className="required">*</span>
          </label>
          <textarea
            id="description"
            {...register('description', { required: 'Description is required' })}
            className={errors.description ? 'error' : ''}
            rows={3}
            placeholder="Describe what this consent is for..."
          />
          {errors.description && (
            <span className="error-message">{errors.description.message}</span>
          )}
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="status">
              Status <span className="required">*</span>
            </label>
            <select id="status" {...register('status', { required: true })}>
              <option value="granted">Granted</option>
              <option value="pending">Pending</option>
              <option value="refused">Refused</option>
              <option value="expired">Expired</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="grantedDate">Granted Date</label>
            <input
              id="grantedDate"
              type="date"
              {...register('grantedDate')}
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="expiryDate">Expiry Date (if time-limited)</label>
          <input
            id="expiryDate"
            type="date"
            {...register('expiryDate')}
          />
        </div>

        <div className="form-group checkbox-group">
          <input
            id="canPatientConsent"
            type="checkbox"
            {...register('canPatientConsent')}
          />
          <label htmlFor="canPatientConsent">Patient can consent</label>
        </div>

        {!canPatientConsent && (
          <div className="proxy-consent-section">
            <h4>Proxy Consent</h4>
            <div className="form-group">
              <label htmlFor="proxyGivenBy">
                Given By <span className="required">*</span>
              </label>
              <input
                id="proxyGivenBy"
                type="text"
                {...register('proxyConsent.givenBy', {
                  required: !canPatientConsent ? 'Required when patient cannot consent' : false,
                })}
                placeholder="Name of person giving consent"
              />
            </div>
            <div className="form-group">
              <label htmlFor="proxyRelationship">
                Relationship <span className="required">*</span>
              </label>
              <input
                id="proxyRelationship"
                type="text"
                {...register('proxyConsent.relationship', {
                  required: !canPatientConsent ? 'Required when patient cannot consent' : false,
                })}
                placeholder="e.g., Guardian, Next of Kin, Power of Attorney"
              />
            </div>
            <div className="form-group">
              <label htmlFor="proxyContactInfo">Contact Info</label>
              <input
                id="proxyContactInfo"
                type="text"
                {...register('proxyConsent.contactInfo')}
                placeholder="Phone or email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="proxyAuthorityDocument">Authority Document</label>
              <input
                id="proxyAuthorityDocument"
                type="text"
                {...register('proxyConsent.authorityDocument')}
                placeholder="e.g., Power of Attorney dated 2024-01-15"
              />
            </div>
            <div className="form-group">
              <label htmlFor="proxyDateOfAuthority">Date of Authority</label>
              <input
                id="proxyDateOfAuthority"
                type="date"
                {...register('proxyConsent.dateOfAuthority')}
              />
            </div>
          </div>
        )}

        <div className="treatment-authority-section">
          <h4>Treatment Authority</h4>
          <div className="form-group">
            <label htmlFor="practitionerName">Practitioner Name</label>
            <input
              id="practitionerName"
              type="text"
              {...register('treatmentAuthority.practitionerName')}
              placeholder="Name of authorized practitioner"
            />
          </div>
          <div className="form-group">
            <label htmlFor="authorityScope">Scope</label>
            <input
              id="authorityScope"
              type="text"
              {...register('treatmentAuthority.scope')}
              placeholder="e.g., All routine treatments, Specific procedure only"
            />
          </div>
        </div>

        <div className="form-group">
          <label htmlFor="restrictions">Restrictions (comma-separated)</label>
          <input
            id="restrictions"
            type="text"
            {...register('restrictions')}
            placeholder="e.g., No invasive procedures, Specific medications only"
          />
        </div>

        <div className="form-group">
          <label htmlFor="notes">Notes</label>
          <textarea
            id="notes"
            {...register('notes')}
            rows={3}
            placeholder="Additional notes..."
          />
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={updateMutation.isPending}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-primary"
            disabled={updateMutation.isPending}
          >
            {updateMutation.isPending ? 'Saving...' : initialData ? 'Update Consent' : 'Add Consent'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

// Withdraw Consent Modal
interface WithdrawConsentModalProps {
  consentId: string
  onConfirm: (reason?: string) => void
  onCancel: () => void
  isSubmitting: boolean
}

function WithdrawConsentModal({ onConfirm, onCancel, isSubmitting }: WithdrawConsentModalProps) {
  const [reason, setReason] = useState('')

  return (
    <Modal
      isOpen={true}
      onClose={onCancel}
      title="Withdraw Consent"
      size="medium"
    >
      <div className="withdraw-consent-modal">
        <p>Are you sure you want to withdraw this consent?</p>
        <div className="form-group">
          <label htmlFor="withdrawReason">Reason (optional)</label>
          <textarea
            id="withdrawReason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="Enter reason for withdrawal..."
          />
        </div>
        <div className="form-actions">
          <button
            type="button"
            onClick={onCancel}
            className="btn-secondary"
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onConfirm(reason || undefined)}
            className="btn-warning"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Withdrawing...' : 'Withdraw Consent'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
